import { captureException, captureMessage, setContext, setTags } from '@sentry/react'
import { notice } from 'app/notice'
import { rest } from 'app/rest'
import { fixImageUrl } from 'component/Image/util'
import { initIrnby } from 'design/irnby'
import { initSela } from 'design/sela'
import { initTsum } from 'design/tsum'
import { personalize_banner } from 'feature/personalize/store'
import { i18n } from 'i18n'
import { initI18next } from 'i18n/config/config'
import { setLang } from 'i18n/config/LanguageDetector'
import { action, comparer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { tele2QrModal, Tele2QrModalStore } from 'page/BasicInferenceScreen/Tele2QrModal/Tele2QrModalStore'
import { api } from 'store/api'
import { FullscreenStore } from 'store/fullscreen'
import { FeedbackStore } from 'store/generation/FeedbackStore'
import { generateLook, GenerateParams } from 'store/generation/generateLook'
import { GeneratingStore } from 'store/generation/GeneratingStore'
import { InferenceCarouselStore } from 'store/generation/InferenceCarouselStore'
import { InferenceTab } from 'store/inference/InferencesStore'
import { InferenceStore } from 'store/inference/InferenceStore'
import { ItemStore } from 'store/item/ItemStore'
import { me } from 'store/me'
import { opt } from 'store/opt'
import ProfileStore from 'store/profile/ProfileStore'
import { socket } from 'store/socket/socket'
import { stat } from 'store/stat'
import { FrameState, ProfileBackAction, store } from 'store/store'
import { buildIdentityUpdate } from 'type/Identity'
import { buildBuyLink, DISLIKE, ItemData, LIKE } from 'type/Inference'
import { buildMetrics } from 'type/metrics'
import { extractItemLookup, FontSettings, ItemParams, Meta, OpenParams, Options, Photo } from 'type/Options'
import { OfferItem } from 'type/rec'
import { BasicSizesRequest } from 'type/size'
import { max } from 'util/array'
import { parseDate } from 'util/date'
import { openLink } from 'util/link'
import { addMessageListener, postMessage, sendEvent } from 'util/message'
import { omit, omitNil } from 'util/object'
import { load, save } from 'util/storage'
import { ym } from 'util/ym'

const ONEST = {
  link: 'https://fonts.googleapis.com/css2?family=Onest:wght@100..900&display=swap',
  family: 'Onest, sans-serif',
}

const INTER = {
  link: 'https://cdn01.sela.ru/assets2/css/fonts.css',
  family: 'Inter, sans-serif',
}

function parseOrigin(location: string) {
  try {
    const url = new URL(location)
    return url.origin
  } catch {
    return undefined
  }
}

class Controller {
  constructor() {
    makeAutoObservable(this)
    me.creator = () => store.create()
  }

  startup(): void {
    addMessageListener('screen', screen => this.onScreen(screen?.width))
    addMessageListener('setup', ({ options, meta }) => this.onSetup(options, meta))
    addMessageListener('state', ({ state }) => this.onState(state))
    addMessageListener('open', params => this.onOpen(params))
    addMessageListener('item', params => this.onItem(params))
    addMessageListener('on-click-buy', () => this.onBuy())
    addMessageListener('reopen', () => this.onReopen())
    addMessageListener('exit', ({ kill }) => this.onExit(kill))
    postMessage('init')
    reaction(() => this.collectUpdate(), update => sendEvent('on-update', update), { equals: comparer.structural })
    reaction(() => this.collectState(), state => sendEvent('on-state', state), { equals: comparer.structural })
    reaction(() => this.collectPhoto(), event => sendEvent('on-photo', event), { equals: comparer.structural })
    this.startVersionCheck()
  }

  private collectPhoto() {
    const photo = store.profile?.picture
    return { photo }
  }

  private collectState() {
    const manyProfiles = store.identities.profiles.filter(it => it.identity_id).length > 1
    const isStartScreen = store.screen === 'start'
    const screen = isStartScreen && manyProfiles ? 'profileSelect' : store.screen
    return {
      window: store.state,
      screen,
    }
  }

  async ensureUser() {
    await me.ensure
  }

  private onScreen = action((width: number) => {
    store.parentWidth = width
  })

  private error(log: string): void {
    console.error(log)
    captureMessage('bad configuration')
    store.setError('bad-configuration')
  }

  private async onSetup(options?: Options, meta?: Meta): Promise<void> {
    setContext('setup', { options, meta })
    await this.trySetup(options, meta)
  }

  private async trySetup(options?: Options, meta?: Meta): Promise<void> {
    try {
      store.setError(undefined)
      if (!options) return this.error('no options')
      if (!meta) return this.error('no meta')
      options = await this.enrich(options, meta)
      await this.setupConfig(options, meta)
      if (!store.error) await this.setupStore(options)
    } catch (e) {
      console.error('setup failed', e)
      captureException(e)
      store.setError('unknown')
    } finally {
      if (store.error) postMessage('setup', { error: true })
    }
  }

  private async setupConfig(options: Options, meta: Meta) {
    const { location } = meta
    if (!location) return this.error('no location meta')

    const origin = parseOrigin(location)
    if (!origin) return this.error('bad location meta')

    const { client, company, access, ws } = options

    if (!client) throw new Error('no client')
    if (!company) throw new Error('no company')
    if (!access) throw new Error('no access')
    if (!ws) throw new Error('no ws')

    store.options = options
    store.meta = meta

    socket.ws = ws
    setTags({ client, company, access, location })
    rest.setup(client, company, access, origin)
    opt.init(options, meta)
  }

  private async enrich(custom: Options, meta: Meta) {
    custom = omitNil(custom)
    const enrich = meta.self ? await api.getConfig(custom) : {}
    return { ...enrich, ...custom }
  }

  private async setupStore(options: Options): Promise<void> {
    me.setup(options)

    if (opt.tsum) initTsum()
    if (opt.irnby) initIrnby()
    if (opt.sela) initSela()

    const lang = options.lang || undefined
    const font = options.font ?? opt.sela ? INTER : ONEST
    setLang(lang)
    const _i18n = initI18next()
    this.setupFont(font)
    const _setup = store.setup()

    await _i18n
    runInAction(() => store.init = true)

    await _setup
    runInAction(() => store.setupDone = true)
    postMessage('setup')
  }

  private onState(state: FrameState) {
    store.state = state
  }

  private onOpen(params?: OpenParams) {
    void store.open(params)
  }

  private async onItem(params?: ItemParams) {
    if (!store.ready || !params) return

    const { callback_id } = params
    if (!callback_id) return

    const { item_id, group_id, limit } = extractItemLookup(params.item)
    if (!item_id) return

    const item = await api.searchItem({ item_id, group_id, limit }).catch(() => undefined)
    postMessage('item', { item, callback_id })
  }

  onBuy() {
    store.customBuy = true
  }

  private onReopen() {
    void store.reopen()
  }

  private onExit(kill?: boolean) {
    this.deleteUser(kill)
    store.comboItemId = undefined
  }

  openFullscreen() {
    if (!store.carousel) return

    const { items, index } = store.carousel.carousel
    const inferences = opt.basic ? items.slice(0, 1) : items
    store.fullscreen = FullscreenStore.fromInferences(inferences, index)
  }

  openFullscreenRecommendations(item: OfferItem) {
    const { items } = store.rec
    const index = items.indexOf(item)
    store.fullscreen = FullscreenStore.fromRecommendations(items, index)
  }

  closeFullscreen() {
    const { inferences } = store.fullscreen ?? {}
    if (inferences && store.carousel) {
      store.carousel.carousel.index = inferences.index
    }
    store.fullscreen = undefined
  }

  openEmailStep() {
    const DAY = 24 * 60 * 60 * 1000
    const date = parseDate(load('email.hide'))?.getTime()
    const hide = date && Date.now() - date < DAY
    if (hide) this.closeProfile()
    else store.profile.step = 'email'
  }

  hideProfile() {
    save('email.hide', new Date().toISOString())
    this.closeProfile()
  }

  closeProfile(): void {
    store.profile.step = 'profile'
    store.profile.simple.stage = 'profile'
    store.identities.reset()
    store.identities.cleanupUnsavedProfiles()

    if (!me.policy) {
      store.screen = 'policy'
      return
    }

    if (this.startBack()) return

    this.openStart()
  }

  private async forceCombo(item_id: string) {
    if (!me.policy) {
      store.screen = 'policy'
      return
    }

    store.screen = 'start'

    if (store.item?.json.item_id !== item_id) {
      await store.loadItem(item_id)
    }

    const item = store.item
    if (!item) return

    const { identity_id, saving } = store.profile
    const good = !!identity_id || !!saving
    const combine = !!opt.options.combine && !!item.json.combine

    if (good && combine && store.available) {
      if (store.screen === 'start') item.openCombine()
    }
  }

  async openStartWithCombo(item_id?: string, combo_item_id?: string) {
    if (!item_id) {
      this.openStart()
      return
    }

    if (!me.policy) {
      store.screen = 'policy'
      return
    }

    store.screen = 'start_combo'

    let item_
    let combo_

    if (store.item?.json.item_id !== item_id) {
      item_ = store.loadItem(item_id)
    }

    const item = store.item
    if (!item) return

    if (combo_item_id) {
      combo_ = store.loadComboItem(combo_item_id)
    }

    await item_
    await combo_

    if (!store.item) {
      store.screen = 'generations'
      return
    }

    if (!store.comboItem) {
      store.screen = 'start'
    }
  }

  openStart(item_id?: string) {
    if (opt.close_back) {
      const start = store.screen === 'start'
      const profile = store.screen === 'profile' && !store.item
      if (start || profile) {
        store.state = 'min'
        return
      }
    }

    if (item_id) void this.forceCombo(item_id)
    else if (store.item) {
      if (!me.policy) {
        store.screen = 'policy'
        return
      }
      store.screen = 'start'
    } else if (store.screen === 'profile') this.openGenerations()
    else if (!store.profile.saving) this.editProfile()
  }

  openProfile(): void {
    this.editProfile()
    store.identities.open = store.profiles
  }

  // Возврат к незаконченному созданию профиля: новый профиль не заводим, иначе
  // потеряется загруженное фото. Экран профиля сбрасывает шаг basic-флоу на первый,
  // поэтому шаг юзера восстанавливаем после смены экрана.
  continueProfile(): void {
    const { basic } = store.profile
    const step = basic.step
    store.screen = 'profile'
    basic.step = step
  }

  editProfile(back?: ProfileBackAction): void {
    if (back) store.profileBack = back
    store.screen = 'profile'
  }

  selectProfile(profile: ProfileStore | undefined) {
    store.profile.reset()
    store.identities.selectProfile(profile)
    store.identities.cleanupUnsavedProfiles()
  }

  openGenerations(): void {
    store.screen = 'generations'
    store.inferences.tab = 'all'
  }

  openFavorites(): void {
    store.screen = 'generations'
    store.inferences.tab = 'fav'
  }

  saveProfileEmail() {
    void store.profile.email.save()
  }

  async saveProfileOnly() {
    await this.saveProfileInner(false)
  }

  async saveProfile() {
    await this.saveProfileInner(false)

    if (opt.basic) {
      await this.generateInner()
      return
    }

    if (this.startBack()) return

    if (!me.policy) {
      store.screen = 'policy'
      return
    }

    if (this.startSingle()) return

    this.openStart()
  }

  private startSingle(): boolean {
    if (store.manualOpenProfile) return false

    const item = store.item
    if (!item) return false

    const { identity_id, saving } = store.profile
    const profile = !!identity_id || !!saving
    const many = store.identities.profiles.filter(i => i.identity_id || i.saving).length > 1
    const combine = !opt.basic && !!opt.options.combine && item.combine && !store.comboItemId

    if (profile && !many && !combine) {
      void this.generateInner()
      return true
    }

    return false
  }

  openSelfieBanner() {
    store.profile.common.banner = 'open'
  }

  closeSelfieBanner() {
    store.profile.common.banner = 'close'
  }

  private async saveProfileInner(_autosave: boolean) {
    const profile = store.profile
    const has_profiles = store.identities.profiles.some(it => it.identity_id)
    const additional_profile = has_profiles && !profile.identity_id
    if (additional_profile) {
      store.profileBack = undefined
    }

    await profile.beforeSave()
    store.identities.activateProfile(profile)
    const previous = profile.saving?.catch(() => undefined)
    const promise = this.saveProfileUnsafe(profile, previous)
    runInAction(() => profile.saving = promise)
    try {
      await promise
    } catch (e) {
      // if (e instanceof SilentError) return
      console.error('save failed', e)
      captureException(e)
      await previous
      if (profile.saving === promise) {
        if (opt.meta.selfie) {
          throw e
        } else {
          notice.error(i18n('FailedToSaveProfile'))
          this.editProfile()
        }
      }
    } finally {
      if (profile.saving === promise) {
        runInAction(() => profile.saving = undefined)
      }
    }
  }

  private async saveProfileUnsafe(profile: ProfileStore, previous: Promise<unknown> | undefined) {
    const create = !profile.identity_id && !profile.saving
    if (create) {
      const request = await profile.buildCreateRequest(me.user_id)
      await me.ensure
      me.updateProfile(buildMetrics(request))
      await previous
      request.user_id = me.user_id
      const response = await api.createIdentity(request)
      sendEvent('on-identity-update', buildIdentityUpdate(response.identity_id, request))
      profile.afterCreate(response)
      store.identities.activateProfile(profile)
      store.identities.cleanupUnsavedProfiles()
    } else {
      const request = await profile.buildUpdateRequest(me.user_id)
      me.updateProfile(buildMetrics(request))
      await previous
      request.identity_id = profile.identity_id
      await api.updateIdentity(request)
      sendEvent('on-identity-update', buildIdentityUpdate(request.identity_id, request))
      profile.afterUpdate()
    }
  }

  async deleteProfile(profile: ProfileStore) {
    store.identities.deleteProfile(profile)
  }

  async generateBasic() {
    // Экран генерации показываем сразу: иначе пока сохраняется профиль и стартует
    // генерация, на экране успевает мелькнуть прошлый результат. Через screen это
    // делать нельзя — его сеттер сбрасывает незасейвленные фото профиля.
    // Ставим флаг первым: дальше любой шаг может кинуть, и экран останется старым.
    store.basicPending = true

    // Сюда приходят только с последнего шага — флоу пройден целиком.
    store.profile.basic.done = true
    store.profile?.applyBasic()
    this.initBasic()

    try {
      await this.saveProfileOnly()
      await this.generateInner()
    } catch (e) {
      // Не пробрасываем: вызывают без обработки, иначе получаем unhandled rejection.
      console.error('basic generation failed', e)
      captureException(e)
    } finally {
      runInAction(() => store.basicPending = false)
    }
  }

  openBasicDebugInference() {
    store.generating = new GeneratingStore(false)
    this.initBasic()
    this.openInference()
  }

  private initBasic() {
    const request = this.buildBasicSizesRequest()
    // Нет данных для подбора размера — это не ошибка, просто сбрасываем размер.
    const promise = request ? api.getBasicSizes(request) : Promise.resolve(undefined)
    void store.basic.init(promise).catch(e => console.error('basic sizes failed', e))
  }

  private buildBasicSizesRequest(): BasicSizesRequest | undefined {
    if (!opt.sizes) return undefined
    const company_id = opt.company
    const item = store.item?.json
    if (!item) return undefined
    const { collection_id, item_id, dress_shape_type } = item
    const { gender, shape, height, weight } = store.profile.basic
    const body_type = shape
    const item_type = dress_shape_type
    if (!gender || !height || !weight) return undefined
    return { company_id, collection_id, item_id, gender, body_type, item_type, height, weight }
  }

  async generateRec() {
    const item = store.tempComboItem ?? store.item
    const rec = item?.rec.item
    await this.generateInner({ rec })
  }

  personalizeInference() {
    const banner = personalize_banner.it
    const { inference, profile } = banner
    const [item_id, combo_item_id] = inference.items.map(it => it.item)

    const item_ = item_id ? api.getItem({ item_id }) : undefined
    const combo_ = combo_item_id ? api.getItem({ item_id: combo_item_id }) : undefined

    const after = async () => {
      if (!item_) return
      store.generating = new GeneratingStore(false)
      store.screen = 'generating'
      personalize_banner.close()
      try {
        const item = await item_
        if (!item) return
        const combo = await combo_
        store.item = new ItemStore(item)
        store.comboItemId = combo?.item_id
        store.comboItem = combo ? new ItemStore(combo) : undefined
        void controller.generateLook()
      } catch (e) {
        console.error(e)
        if (store.generating) {
          store.generating.error = true
        }
      }
    }

    store.closeProfileCallback = item_id ? after : undefined
    profile.common.userPhoto.setUrl(undefined)
    store.identities.activateProfile(profile)
    banner.photo = true
  }

  async generateLook() {
    await this.generateInner()
  }

  async generateSingleItem(single: ItemStore | undefined) {
    if (!single) {
      store.screen = 'start'
    } else {
      await this.generateInner({ single })
    }
  }

  openCombineScreen() {
    const item = store.item
    if (!item) return
    item.openCombine()
    store.screen = 'combine_select'
  }

  async openCombineForItem(item: ItemStore | undefined) {
    if (!me.policy) {
      store.screen = 'policy'
    } else if (!item) {
      store.screen = 'start'
    } else {
      if (item.combine) {
        store.tempComboItem = item
        item.openCombine()
      }
    }
  }

  // авто комбинаторика
  async handlePendingComboGeneration() {
    const { comboItemId } = store
    if (!comboItemId) return

    try {
      const item = store.item
      if (!item || !item.json.combine) {
        console.warn('Main item does not support combining')
        return
      }

      const { identity_id, saving } = store.profile
      const good = !!identity_id || !!saving
      if (!good) {
        return
      }

      if (!store.available) {
        console.warn('Generation limit reached')
        store.screen = 'start'
        store.state = 'max'
        return
      }

      await store.loadComboItem(comboItemId)

      if (!store.comboItem) {
        console.warn('Combo item not found:', comboItemId)
        return
      }

      // В basic 'start' превращается в шаг профиля — он мелькнул бы перед генерацией.
      if (!opt.basic) store.screen = 'start'

      await this.generateInner()

    } catch (e) {
      console.error('Pending combo generation failed:', e)
      captureException(e)
    }
  }

  private async generateInner(params?: GenerateParams) {
    if (!store.available) {
      console.warn('Generation limit reached')
      store.screen = 'start'
      store.state = 'max'
    } else if (!me.policy) {
      store.policyGenerateParams = params
      store.screen = 'policy'
    } else {
      await generateLook(params)
    }
  }

  openInference(inference?: InferenceStore, tab?: InferenceTab) {
    if (tab) store.inferences.tab = tab
    const inferences = store.inferences.listTabInferences
    store.carousel = new InferenceCarouselStore(inference, inferences)
    store.screen = 'image'
  }

  viewInference(inference: InferenceStore | undefined): void {
    if (!inference) return

    const { inference_id, item_id, collection_id, complete, viewed, items } = inference
    if (!inference_id || !item_id || !complete || viewed) return

    inference.onView()
    void api.viewInference(inference_id)
    stat.view(item_id, collection_id, inference_id)
    sendEvent('on-view-outfit', { inference_id, items })
  }

  createNewOutfit(inference: InferenceStore) {
    const { items } = inference
    const item_id = items[0]?.item
    const combo_item_id = items[1]?.item

    controller.closeBuyBanner()
    if (combo_item_id) controller.openStartWithCombo(item_id, combo_item_id)
    else controller.openStart(item_id)
  }

  async likeInference(inference: InferenceStore, like: boolean) {
    const { inference_id, items } = inference
    sendEvent(like ? 'on-like' : 'on-dislike', { inference_id, items })
    inference.toggleLike(like)
    const { image_id, like_mode } = inference
    if (like_mode === LIKE) store.carousel.showMessage(i18n('AddedToFavorites'))
    if (like_mode === DISLIKE) store.carousel.feedback = new FeedbackStore(inference)
    if (like_mode) this.sendLikeStat(inference, like)
    if (image_id) await api.setInferenceImageLikeMode(image_id, like_mode)
  }

  deleteInference(inference: InferenceStore) {
    store.inferences.deleteInference(inference)
    store.carousel?.deleteInference(inference)
    store.fullscreen?.inferences?.deleteItem(inference)
    const items = store.carousel?.carousel.items ?? []
    const empty_fullscreen = !store.fullscreen?.inferences?.items.length
    const empty = !items.length
    const another = store.inferences.listAll[0]
    if (empty_fullscreen) {
      store.fullscreen = undefined
    }
    if (empty && store.screen === 'image') {
      if (opt.tsum) {
        if (another) {
          this.openInference(another, 'all')
        } else {
          store.screen = store.item ? 'start' : 'profile'
        }
      } else {
        store.screen = 'generations'
      }
    }
  }

  private sendBuyStat(inference: InferenceStore) {
    const { inference_id, identity_id, item_id, collection_id, image_id, items } = inference
    stat.buy(item_id, collection_id, identity_id, inference_id, image_id)
    sendEvent('on-click-buy', { inference_id, items })
  }

  private sendLikeStat(inference: InferenceStore, like: boolean) {
    const { inference_id, identity_id, item_id, collection_id, image_id } = inference
    if (like) stat.like(item_id, collection_id, identity_id, inference_id, image_id)
    else stat.dislike(item_id, collection_id, identity_id, inference_id, image_id)
  }

  private setupFont(font: FontSettings) {
    if (font.link) {
      const link = document.createElement('link')
      link.href = font.link
      link.rel = 'stylesheet'
      document.head.append(link)
    }

    if (font.family) {
      const style = document.createElement('style')
      style.textContent = `body { font-family: ${font.family}; }`
      document.head.append(style)
    }
  }

  async deleteUser(kill?: boolean) {
    // Для telegram и для встройки с внешним user id локальной очистки мало:
    // при следующем запуске сервер вернёт того же пользователя — чистим данные через API
    if (store.tgWebAppData || me.external) await store.resetRemoteData()
    me.deleteUser(kill)
    postMessage('exit')
    if (!kill) await store.reset()
  }

  openBuy(inference: InferenceStore, item?: ItemData) {
    item ??= inference.items[0]
    if (!item) return

    const links = item.links || inference.links
    const link = buildBuyLink(links, inference.inference_id)

    if (store.customBuy) {
      // external custom handler
      this.sendBuyStat(inference)
      return
    }

    if (!link) return

    this.sendBuyStat(inference)
    openLink(link)
  }

  openLookyFoo(inference: InferenceStore) {
    const query = new URLSearchParams(omit({
      media: fixImageUrl(inference.source),
      type: 'photo',
      caption: '#чемпионат_мира_2026',
      externalLink: 'https://window.dresscode.ai/lookychampionship',
      externalLinkDecor: 'world_cup',
      targetRatio: 'A9x16',
    }))
    const link = `rn-looky://navigate/creativity/post?${query}`

    this.sendBuyStat(inference)
    openLink(link)
  }

  openTele2Qr() {
    const { item } = store.carousel.carousel
    if (item) this.sendBuyStat(item)
    tele2QrModal.open(new Tele2QrModalStore())
  }

  openBuyBanner(inference: InferenceStore) {
    store.inferences.buy = inference
  }

  closeBuyBanner() {
    store.inferences.buy = undefined
  }

  openCreateProfile(back?: ProfileBackAction) {
    if (back) store.profileBack = back

    if (opt.basic) {
      const current = store.profile?.identity_id ? store.profile.type : undefined
      this.openNewProfile(current ?? opt.photo_def)
    } else if (opt.photo.length > 1) {
      store.createPhoto = opt.photo_def
      store.createProfile = true
    } else {
      this.openNewProfile(opt.photo_def)
    }
  }

  openNewProfile(type: Photo) {
    this.selectProfile(undefined)

    if (store.profile) {
      runInAction(() => {
        store.profile.isNew = true
        store.profile.type = type
        store.profile.common.userPhoto.clear()
      })
    }

    this.editProfile()
  }

  closeCreateProfile() {
    store.createProfile = false
  }

  async acceptPolicy() {
    await me.ensure
    me.policy = true
    void api.updateUser({ personal_data_consent: 1 })

    if (store.profileBack) {
      if (this.startBack()) return
    }

    if (store.policyGenerateParams) {
      const params = store.policyGenerateParams
      store.policyGenerateParams = undefined
      void this.generateInner(params)
      return
    }

    if (store.item) {
      const { identity_id, saving } = store.profile
      const hasProfile = !!identity_id || !!saving

      store.screen = 'start'

      if (hasProfile) {
        if (store.comboItemId) {
          void this.handlePendingComboGeneration()
        }
      } else {
        this.openCreateProfile()
      }
    } else {
      store.screen = 'generations'
      if (!store.identity_id) this.openCreateProfile()
    }
  }

  private collectUpdate() {
    const { user_id } = me
    if (!user_id) return {}

    const { profile, inferences } = store
    const { gender, picture } = profile ?? {}
    const { listAll, listFav } = inferences
    const outfits = listAll.length
    const favorites = listFav.length
    const last_time = max(listAll.filter(it => it.complete).map(it => it.created_at))

    return { user_id, gender, picture, outfits, favorites, last_time }
  }

  planGeneration(back: ProfileBackAction) {
    if (!me.policy) {
      store.screen = 'policy'
      store.profileBack = back
    } else if (!store.available) {
      store.screen = 'start'
    } else {
      this.openCreateProfile(back)
    }
  }

  private startBack(): boolean {
    const item = store.item
    const back = store.profileBack
    const { identity_id, saving } = store.profile
    const good = !!identity_id || !!saving

    const callback = store.closeProfileCallback
    if (callback) {
      store.closeProfileCallback = undefined
      callback()
      return true
    }

    if (!item) return false

    if (back === 'generate') {
      if (good && store.available) {
        store.profileBack = undefined
        void this.generateLook()
        return true
      }
    }

    if (back === 'combine') {
      const combine = !!opt.options.combine && !!item?.json.combine
      if (good && combine && store.available) {
        this.openStart()
        if (store.comboItemId) {
          void this.handlePendingComboGeneration()
          return true
        } else if (store.screen === 'start') {
          store.profileBack = undefined
          this.openCombineScreen()
          return true
        }
      }
    }

    return false
  }

  private startVersionCheck() {
    let version = ''

    async function update() {
      const update = await api.getVersion()
      if (update && update !== version) {
        version = update
        postMessage('version', { version })
      }
    }

    setInterval(update, 10 * 60 * 1000)
    update()
  }

  onShare(destination: string, inference: InferenceStore) {
    const { inference_id, identity_id, item_id, collection_id, image_id, items } = inference
    stat.share(item_id, collection_id, identity_id, inference_id, image_id, destination)
    sendEvent('on-share-outfit', { destination, inference_id, items })
  }

  onClickTry() {
    const item = store.item?.json
    ym('click_try_on', { from: 'widget_card' })
    sendEvent('on-click-try-on', { item })
  }
}

export const controller = new Controller()
